import { SpinnerButton } from "components/shared/SpinnerButton";
import { ErrorMessage } from "components/shared/ErrorMessage";
import { Message } from "shared/components/Message";
import React, { ReactElement, ReactNode, useState } from "react";
import { MessageDescriptor } from "react-intl";
import { useRelayEnvironment } from "react-relay";
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import Button from "reactstrap/lib/Button";
import { IEnvironment, PayloadError } from "relay-runtime";
import { sharedMessages } from "shared/sharedMessages";
import { DefaultValues, FieldValues, FormProvider, useForm, UseFormReturn } from "react-hook-form";

export interface IWarningMessageProps {
    titleMessage: string;
    warningMessage: ReactNode;
    buttons?: (options: {
        cancelAction: () => void;
        submitAction: () => void
        renderCancelButton: () => ReactNode;
        renderSubmitButton: () => ReactNode;
    }) => ReactElement;
}

type MutationFunc<TRequest, TResponse> = (environment: IEnvironment, request: TRequest, onCompleted: (response: TResponse, errors?: PayloadError[] | null) => void, onError: (error: Error) => void) => void;

type RenderPropChildren<T extends FieldValues> = ((props: Pick<UseFormReturn<T>, "control" | "register">) => React.ReactNode);
type Children<T extends FieldValues> = React.ReactNode | RenderPropChildren<T>;

interface IProps<TRequest extends FieldValues, TResponse> {
    backButtonMessage?: MessageDescriptor;
    children?: Children<TRequest>;
    defaultValues?: DefaultValues<TRequest>;
    nextButtonMessage?: MessageDescriptor;
    onNext?: (response: TResponse | null) => void;
    onPrevious?: () => void;
    onError?: (errors: PayloadError[] | Error) => void;
    saveButtonMessage?: MessageDescriptor;
    alwaysSubmit?: boolean;
    shouldDisplayWarning?: (values: TRequest) => IWarningMessageProps[] | false;
    toastSuccess?: () => void;
    toastFailure?: () => void;
}

interface IInjectedProps<TRequest, TResponse> {
    mutationFunc: MutationFunc<TRequest, TResponse>;
}

export function ErrorHandlingMutationForm<TRequest extends FieldValues, TResponse>(props: IProps<TRequest, TResponse> & IInjectedProps<TRequest, TResponse>) {
    const {
        backButtonMessage,
        children,
        defaultValues,
        nextButtonMessage,
        onPrevious,
        saveButtonMessage,
        shouldDisplayWarning,
        toastSuccess,
        toastFailure,
    } = props;

    const environment = useRelayEnvironment();
    const [errors, setErrors] = useState<PayloadError[] | Error | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [warningMessages, setWarningMessages] = useState<IWarningMessageProps[]>([]);

    const useFormReturn = useForm<TRequest>({ defaultValues, mode: "onBlur" });
    const {
        control,
        formState: { isDirty, errors: validationErrors },
        handleSubmit,
        register,
    } = useFormReturn;

    return <FormProvider {...useFormReturn}>
        <Form onSubmit={submitForm} noValidate={true} role="form">
            {typeof children === "function" ? children({ control, register }) : children}
            {
                renderErrors()
            }
            <div>
                {onPrevious && <Button
                    color="secondary"
                    type="button"
                    className="float-left"
                    onClick={onPrevious}>
                    <Message message={backButtonMessage || sharedMessages.backButtonLabel} />
                </Button>}
                {shouldShowSubmitButton()
                    ? <SpinnerButton type="submit" className="float-right" color="primary" isLoading={isSubmitting} message={saveButtonMessage || sharedMessages.saveLabel} />
                    : <Button
                        className="float-right"
                        color="primary"
                        type="button"
                        onClick={onNext}>
                        <Message message={nextButtonMessage || sharedMessages.nextButtonLabel} />
                    </Button>
                }
            </div>
            <Modal isOpen={warningMessages.length > 0} toggle={toggleWarningMessage} labelledBy="formWarning">
                <ModalHeader toggle={toggleWarningMessage} id="formWarning">
                    {warningMessages[0]?.titleMessage}
                </ModalHeader>
                <ModalBody>
                    {warningMessages[0]?.warningMessage}
                </ModalBody>
                <ModalFooter>
                    {
                        warningMessages[0]?.buttons
                            ? warningMessages[0].buttons({
                                cancelAction: toggleWarningMessage,
                                submitAction: submitForm,
                                renderCancelButton,
                                renderSubmitButton: renderSubmitButton(),
                            })
                            : <>
                                {renderSubmitButton()()}
                                {renderCancelButton()}
                            </>
                    }
                </ModalFooter>
            </Modal>
        </Form>
    </FormProvider>;

    function submitForm(e?: React.FormEvent<HTMLFormElement>) {
        handleSubmit(onSubmit)(e);
    }

    function renderSubmitButton() {
        return () => {
            return <SpinnerButton
                color="primary"
                isLoading={isSubmitting}
                message={saveButtonMessage || sharedMessages.saveLabel}
                onClick={() => submitForm()}
            />;
        };
    }

    function renderCancelButton() {
        return <Button type="button" color="secondary" onClick={toggleWarningMessage}>
            <Message message={sharedMessages.cancelButtonLabel} />
        </Button>;
    }

    function toggleWarningMessage() {
        setWarningMessages(messages => {
            return messages.slice(1);
        });
    }

    function onNext() {
        if (props.onNext) {
            props.onNext(null);
        }
    }

    function onSubmit(request: TRequest) {
        if (warningMessages.length === 0 && shouldDisplayWarning) {
            const warningProps = shouldDisplayWarning(request);

            if (warningProps) {
                setWarningMessages(warningProps);
                return;
            }
        }

        if (warningMessages.length > 1) {
            toggleWarningMessage();
            return;
        }

        setIsSubmitting(true);
        setWarningMessages([]);
        try {
            props.mutationFunc(environment, request, onResponse, onMutationError);
        }
        catch {
            setIsSubmitting(false);
        }
    }

    function onResponse(response: TResponse, payloadErrors?: PayloadError[] | null) {
        if (payloadErrors) {
            if (props.onError) {
                props.onError(payloadErrors);
            }

            if (toastFailure) {
                toastFailure();
            }

            setErrors(payloadErrors);
            setIsSubmitting(false);
        } else if (props.onNext) {
            if (toastSuccess) {
                toastSuccess();
            }

            setIsSubmitting(false);
            props.onNext(response);
        } else {
            if (toastSuccess) {
                toastSuccess();
            }

            setIsSubmitting(false);
        }
    }

    function onMutationError(error: Error) {
        if (props.onError) {
            props.onError(error);
        }

        if (toastFailure) {
            toastFailure();
        }

        setErrors(error);
        setIsSubmitting(false);
    }

    function shouldShowSubmitButton() {
        return props.alwaysSubmit || (!props.onNext || isDirty || Object.keys(validationErrors).length > 0);
    }

    function renderErrors() {
        if (errors) {
            return <ErrorMessage heading={sharedMessages.requestFailedTitle} message={sharedMessages.saveFailedErrorMessage} />;
        }

        return null;
    }
}
